import Vue from 'vue'
import Vuex from 'vuex'
import { isSoldOut, formatPrice, exception } from '@/utils'

// Event Bus
const eventBus = new Vue()

// Shopify Client
let Shopify
async function getClient () {
  if (Shopify) return
  try {
    // disable code-split...
    // const { default: shopify } = await import(/* webpackChunkName: 'shopify' */ '@/shopify-client')
    const { default: shopify } = await import('@/shopify-client')
    Shopify = shopify
  } catch (err) {
    console.error(err)
    exception('Error: getClient - ' + err.message, true)
  }
}

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    bus: eventBus,
    winW: window.innerWidth,
    winH: window.innerHeight,

    site: null,
    pages: {},
    products: [],
    racks: {}, // wp shop post type
    collections: {}, // shopify collections

    cart: false, // visibility
    checkout: null,
    checkoutUpdating: false,
    policies: {}
  },
  getters: {
    winIs: (state) => (name) => {
      // match src/style/variables.css
      return name === 'xl' ? state.winW >= 1440
        : name === 'lg' ? state.winW >= 1024
          : name === 'md' ? state.winW >= 813
            : name === 'sm' ? state.winW >= 415
              : name === 'portrait' ? state.winW < state.winH
                : undefined
    },
    cartCount (state) {
      return state.checkout && state.checkout.lineItems.reduce((acc, curr) => acc + curr.quantity, 0)
    },
    price: (state) => (variant) => {
      const soldOut = isSoldOut(variant)
      return {
        price: formatPrice(variant.price),
        compareAt: formatPrice(variant.compareAtPrice),
        reduced: Number(variant.price) < Number(variant.compareAtPrice),
        avail: !soldOut,
        msg: soldOut ? 'Sold Out' : soldOut === undefined ? 'N/A' : ''
      }
    },
    meta: (state) => ({ title, descrip, img }) => {
      const meta = []
      // const site = state.site
      title = title ? `${title} - OTTOLINGER` : 'OTTOLINGER'
      // descrip = descrip || _get(site, 'data.meta_description')
      // img = img || _get(site, 'data.meta_site_image.url')
      // ADD
      meta.push({ property: 'og:title', content: title })
      meta.push({ property: 'og:site_name', content: 'OTTOLINGER' })
      meta.push({ property: 'og:type', content: 'website' })
      if (descrip) {
        meta.push({ name: 'description', content: descrip })
        meta.push({ property: 'og:description', content: descrip })
      }
      if (img) {
        meta.push({ property: 'og:image', content: img })
      }
      // meta.push({ property: 'og:url', content: ##ADDCANNONICAL## })
      return meta
    }
  },
  mutations: {
    add (state) {
      state.count++
      state.cart = true
    },
    SET_WIN_W (state, val) {
      state.winW = val
    },
    SET_WIN_H (state, val) {
      state.winH = val
    },
    showCart (state) {
      state.cart = true
    },
    hideCart (state) {
      state.cart = false
    },
    SET_SITE (state, doc) {
      state.site = doc
    },
    ADD_PAGE (state, { slug, data }) {
      Vue.set(state.pages, slug, data)
    },
    ADD_PRODUCT (state, itm) {
      state.products.push(itm)
    },
    ADD_RACK (state, { slug, data }) {
      Vue.set(state.racks, slug, data)
    },
    ADD_COLLECTION (state, { slug, data }) {
      Vue.set(state.collections, slug, data)
    },
    CHECKOUT_UPDATING (state) {
      state.checkoutUpdating = true
    },
    SET_CHECKOUT (state, c) {
      state.checkout = c
      state.checkoutUpdating = false
    },
    SET_POLICIES (state, p) {
      state.policies = p
    }
  },
  actions: {
    async getGlobals ({ state, commit }) {
      return fetch(Vue.config.WP.ACF + '/options/options')
        .then(resp => resp.json())
        .then(({ acf }) => {
          commit('SET_SITE', acf)
        })
        .catch(e => console.error(e))
    },
    async getPage ({ state, commit }, slug) {
      const saved = state.pages[slug]
      if (saved) return saved
      return fetch(Vue.config.WP.API + `/pages?slug=${slug}`)
        .then(resp => resp.json())
        .then(data => {
          commit('ADD_PAGE', { slug: slug, data: data[0] })
          return state.pages[slug]
        })
        .catch(e => console.error(e))
    },
    async getRack ({ state, commit }, { slug, pwd, refresh = false }) {
      const saved = state.racks[slug]
      if (saved && !refresh) return saved
      return fetch(Vue.config.WP.API + `/shop?slug=${slug}&password=${pwd}`)
        .then(resp => resp.json())
        .then(data => {
          commit('ADD_RACK', { slug: slug, data: data[0] })
          return data[0]
        })
        .catch(e => console.error(e))
    },
    async getProduct ({ state, commit }, handle) {
      const saved = state.products.find(p => p.handle === handle)
      if (saved) return saved
      await getClient()
      return Shopify.product.fetchByHandle(handle)
        .then(p => {
          // TODO, maybe don't save to store...
          commit('ADD_PRODUCT', p)
          return p
        })
        .catch(e => console.error(e))
    },
    async getCollection ({ state, commit }, slug) {
      const saved = state.collections[slug]
      if (saved) return saved
      const id = btoa(`gid://shopify/Collection/${slug}`)
      await getClient()
      return Shopify.collection.fetchWithProducts(id, { productsFirst: 150 })
        .then(c => commit('ADD_COLLECTION', { slug: slug, data: c }))
        .catch(err => {
          console.error(`Failed to fetch Shopify collection (slug: ${slug})\n`, err)
        })
    },

    // cart
    async createCheckout ({ dispatch, commit, state }, logSuccess = false) {
      await getClient()
      const savedId = sessionStorage.getItem('checkoutId')
      if (savedId) {
        return Shopify.checkout.fetch(savedId).then(checkout => {
          // missing checkout
          if (!checkout) {
            return dispatch('resetCheckout', 'Checkout missing')
          }
          // already completed
          if (checkout && checkout.completedAt) {
            return dispatch('resetCheckout', 'Checkout completed at: ', checkout.completedAt)
          }
          // items no longer available
          const itemUnavailable = checkout.lineItems && checkout.lineItems.find(itm => !itm.variant || !itm.variant.available)
          if (itemUnavailable) {
            return dispatch('resetCheckout', 'Checkout at least 1 item unavialable')
          }
          // use saved Checkout
          commit('SET_CHECKOUT', checkout)
        }, err => {
          console.error(err)
          exception(`Error: createCheckout (fetch saved cart) - ${err[0].message}`)
          return dispatch('resetCheckout', 'Fetch cart by ID failed. Creating new...')
        })
      }
      // else, create new
      return Shopify.checkout.create().then((checkout) => {
        sessionStorage.setItem('checkoutId', checkout.id) // save to sessionStorage
        commit('SET_CHECKOUT', checkout)
        if (logSuccess) console.log('New cart Success')
      }, err => {
        console.error(err)
        exception(`Error: createCheckout - ${err.message}`)
      })
    },
    async resetCheckout ({ dispatch }, msg) {
      console.log('Reset Checkout:', msg)
      sessionStorage.removeItem('checkoutId')
      return dispatch('createCheckout', true)
    },
    async addToCart ({ state, commit }, id) {
      await getClient()
      const lineItems = [{ variantId: id, quantity: 1 }] //, customAttributes: [{key: 'date', value: '2018-04-15'}] }]
      commit('CHECKOUT_UPDATING')
      return Shopify.checkout.addLineItems(state.checkout.id, lineItems)
        .then(checkout => {
          commit('SET_CHECKOUT', checkout)
          commit('showCart')
          return checkout
        }, err => {
          console.error(err)
          exception(`Error: addToCart – ${err.message}`)
        })
    },
    async updateCartItems ({ state, commit }, lineItems) {
      await getClient()
      commit('CHECKOUT_UPDATING')
      return Shopify.checkout.updateLineItems(state.checkout.id, lineItems)
        .then(checkout => {
          commit('SET_CHECKOUT', checkout)
          return checkout
        }, err => console.error(err))
    },
    async getPolicies ({ commit }) {
      await getClient()
      return Shopify.shop.fetchPolicies().then(shop => commit('SET_POLICIES', shop))
    },

    // newsletter
    async subscribeToNewsletter (_, email) {
      try {
        let resp = await fetch('/.netlify/functions/mailchimp-subscriber-add', { method: 'POST', body: JSON.stringify({ email }) })
        resp = await resp.json()
        if (resp.title === 'Member Exists') {
          return { status: 400, message: 'Already subscribed' }
        } else if (resp.status === 200 || resp.status === 'subscribed') {
          return { status: 200, message: 'Success' }
        } else {
          console.error('@subscribeToNewsletter', resp)
          return { status: 500, message: 'Error' }
        }
      } catch (e) {
        console.error('@subscribeToNewsletter', e)
        return { status: 500, message: e.message || 'Error' }
      }
    }
  }
})
